import { buildCreditCheckInfo } from 'journey/checkout/hooks/useSubmitOrder/utils/buildTrackingTransactionData.utils';
import { CartTrackingProps, TrackingCartBundles } from '../../types/cart';
import {
  buildCartProductData,
  buildDiscountData,
  getFrequencyCostSummary,
} from './buildTrackingProductData.utils';
import { DEFAULT_TRACKING_PRICE, GIFT_PRODUCT } from './tracking.constants';

export const isPromotion = (bundles?: TrackingCartBundles) => {
  return bundles.some((bundle) =>
    bundle.items.some((item) => item?.dataLayerCategory === GIFT_PRODUCT),
  );
};

export function buildTrackingCartData(buildCartDataProps: CartTrackingProps) {
  const {
    cart: { summaries, cartId, bundles, auth },
    isBusiness,
    connectionData,
    creditCheckOutcome,
    stockInfo,
  } = buildCartDataProps;

  const hasPromotion = isPromotion(bundles);

  const isExistingCustomer = !auth?.isGuest;

  const oneOffCostSummary = getFrequencyCostSummary(summaries, 'ONE_OFF');
  const oneOffCostSummaryTotal = getFrequencyCostSummary(
    summaries,
    'ONE_OFF_TOTAL',
  );
  const oneOffCostTotal = oneOffCostSummaryTotal
    ? `${oneOffCostSummaryTotal.total}`
    : DEFAULT_TRACKING_PRICE;
  const oneOffDiscount = oneOffCostSummary.discount
    ? `${oneOffCostSummary.discount}`
    : DEFAULT_TRACKING_PRICE;

  const monthlyCostSummary = getFrequencyCostSummary(summaries, 'MONTHLY');
  const monthlyCostTotal = monthlyCostSummary.total
    ? `${monthlyCostSummary.total}`
    : DEFAULT_TRACKING_PRICE;
  const monthlyDiscount = monthlyCostSummary.discount
    ? `${monthlyCostSummary.discount}`
    : DEFAULT_TRACKING_PRICE;

  const totalSummary = oneOffCostSummaryTotal.total + monthlyCostSummary.total;
  const totalCost = totalSummary ? `${totalSummary}` : DEFAULT_TRACKING_PRICE;

  const totalSubtotalSummary =
    oneOffCostSummaryTotal.subtotal + monthlyCostSummary.subtotal;
  const subtotalCost = totalSubtotalSummary
    ? `${totalSubtotalSummary}`
    : DEFAULT_TRACKING_PRICE;

  const productDataItem = bundles
    .map((bundle) => {
      const { connectionType, currentProvider } =
        connectionData?.find(
          (connection) => connection.bundleId === bundle.bundleId,
        ) || {};

      return buildCartProductData({
        bundle,
        summaries,
        isBusiness,
        ...(connectionData && { connectionType, currentProvider }),
      });
    })
    .flat();

  const upfrontTotal =
    String(
      summaries.find((summary) => summary.billingFrequency === 'UP_FRONT')
        ?.total,
    ) ?? '0.00';

  const creditCheckInfo = creditCheckOutcome
    ? buildCreditCheckInfo({
        creditCheckOutcome,
        upfrontTotal,
      })
    : undefined;

  return {
    cartID: cartId,
    attributes: {
      baseStore: isBusiness ? 'businessshop' : 'personalshop',
      cartTotalOneOffCost: oneOffCostTotal,
      cartTotalOneOffCostDiscount: oneOffDiscount,
      cartTotalRecurringCost: monthlyCostTotal,
      cartTotalRecurringCostDiscount: monthlyDiscount,
      isExistingCustomer,
      isGiftWithPurchase: hasPromotion,
      numOfBundles: `${bundles.length}`,
      stockInfo,
      discountInfo: buildDiscountData(productDataItem),
      creditCheckInfo,
      isChangePlan:
        bundles?.filter((bundle) => {
          return bundle?.processContext === 'CHANGE_OFFER';
        })?.length > 0,
    },
    price: {
      basePrice: subtotalCost,
      cartTotal: totalCost,
      currency: 'NZD',
    },
    item: productDataItem,
  };
}

export function buildTrackingSaveCartData(
  buildCartDataProps: CartTrackingProps,
) {
  const { saveCartEmail, saveCartOptIn } = buildCartDataProps;
  const trackingCartData = buildTrackingCartData(buildCartDataProps);
  return {
    ...trackingCartData,
    attributes: {
      ...trackingCartData.attributes,
      saveCartEmail,
      saveCartOptIn,
    },
  };
}
